
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Auth() {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'phone' | 'verification' | 'profile'>('phone');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phoneNumber.length !== 8) {
      alert('يرجى إدخال رقم جوال صحيح مكون من 8 أرقام');
      return;
    }
    
    setIsLoading(true);
    // محاكاة إرسال رمز التحقق
    setTimeout(() => {
      setIsLoading(false);
      setStep('verification');
    }, 2000);
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      alert('يرجى إدخال رمز التحقق المكون من 6 أرقام');
      return;
    }
    
    setIsLoading(true);
    // محاكاة التحقق من الرمز
    setTimeout(() => {
      setIsLoading(false);
      setStep('profile');
    }, 1500);
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      alert('يرجى إدخال الاسم');
      return;
    }
    
    setIsLoading(true);
    // محاكاة إنشاء الحساب
    setTimeout(() => {
      setIsLoading(false);
      navigate('/');
    }, 1500);
  };

  const formatPhoneNumber = (value: string) => {
    // إزالة جميع الأحرف غير الرقمية
    const numbers = value.replace(/\D/g, '');
    // تحديد الطول إلى 8 أرقام
    return numbers.slice(0, 8);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" style={{ fontFamily: 'Cairo, sans-serif' }}>
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-4">
        <button 
          onClick={() => navigate('/')}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-900"
        >
          <i className="ri-arrow-right-line text-xl"></i>
        </button>
        <h1 className="text-lg font-semibold">إنشاء حساب جديد</h1>
        <div className="w-10"></div>
      </header>

      <div className="flex-1 px-4 py-6">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
              step === 'phone' ? 'bg-yellow-500 text-black' : 'bg-green-500 text-white'
            }`}>
              {step === 'phone' ? '1' : <i className="ri-check-line"></i>}
            </div>
            <div className={`w-12 h-1 ${step === 'verification' || step === 'profile' ? 'bg-yellow-500' : 'bg-gray-700'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
              step === 'verification' ? 'bg-yellow-500 text-black' : 
              step === 'profile' ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-400'
            }`}>
              {step === 'profile' ? <i className="ri-check-line"></i> : '2'}
            </div>
            <div className={`w-12 h-1 ${step === 'profile' ? 'bg-yellow-500' : 'bg-gray-700'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
              step === 'profile' ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-gray-400'
            }`}>
              3
            </div>
          </div>
        </div>

        {/* Phone Number Step */}
        {step === 'phone' && (
          <div className="max-w-sm mx-auto">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                <i className="ri-phone-line text-3xl text-black"></i>
              </div>
              <h2 className="text-xl font-bold mb-2">رقم الجوال</h2>
              <p className="text-gray-400 text-sm">سنرسل لك رمز التحقق عبر الرسائل النصية</p>
            </div>

            <form onSubmit={handlePhoneSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">رقم الجوال</label>
                <div className="relative">
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
                    <img 
                      src="https://public.readdy.ai/ai/img_res/6a760a1a6f1d91fb2f52f7ee856638ad.jpg"
                      alt="Qatar"
                      className="w-6 h-4 ml-2"
                    />
                    <span className="text-gray-400 text-sm">+974</span>
                  </div>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="12345678"
                    className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 pr-20 text-right focus:outline-none focus:border-yellow-500"
                    maxLength={8}
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">مثال: 12345678</p>
              </div>

              <button
                type="submit"
                disabled={phoneNumber.length !== 8 || isLoading}
                className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin ml-2"></div>
                    جاري الإرسال...
                  </div>
                ) : (
                  'إرسال رمز التحقق'
                )}
              </button>
            </form>
          </div>
        )}

        {/* Verification Step */}
        {step === 'verification' && (
          <div className="max-w-sm mx-auto">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                <i className="ri-shield-check-line text-3xl text-black"></i>
              </div>
              <h2 className="text-xl font-bold mb-2">رمز التحقق</h2>
              <p className="text-gray-400 text-sm">
                تم إرسال رمز التحقق إلى
                <br />
                <span className="text-white font-medium">+974 {phoneNumber}</span>
              </p>
            </div>

            <form onSubmit={handleVerificationSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">رمز التحقق</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-center text-2xl tracking-widest focus:outline-none focus:border-yellow-500"
                  maxLength={6}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={verificationCode.length !== 6 || isLoading}
                className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin ml-2"></div>
                    جاري التحقق...
                  </div>
                ) : (
                  'تحقق من الرمز'
                )}
              </button>

              <button
                type="button"
                className="w-full text-yellow-400 text-sm py-2"
                onClick={() => setStep('phone')}
              >
                إعادة إرسال الرمز
              </button>
            </form>
          </div>
        )}

        {/* Profile Step */}
        {step === 'profile' && (
          <div className="max-w-sm mx-auto">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                <i className="ri-user-add-line text-3xl text-black"></i>
              </div>
              <h2 className="text-xl font-bold mb-2">إكمال الملف الشخصي</h2>
              <p className="text-gray-400 text-sm">أدخل بياناتك لإنشاء حسابك</p>
            </div>

            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">الاسم الكامل</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="أدخل اسمك الكامل"
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-right focus:outline-none focus:border-yellow-500"
                  required
                />
              </div>

              <div className="bg-gray-900 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">رقم الجوال</span>
                  <button
                    type="button"
                    onClick={() => setStep('phone')}
                    className="text-yellow-400 text-sm"
                  >
                    تغيير
                  </button>
                </div>
                <p className="text-gray-400 text-sm">+974 {phoneNumber}</p>
              </div>

              <button
                type="submit"
                disabled={!name.trim() || isLoading}
                className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin ml-2"></div>
                    جاري إنشاء الحساب...
                  </div>
                ) : (
                  'إنشاء الحساب'
                )}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Terms */}
      <div className="px-4 pb-6">
        <p className="text-xs text-gray-500 text-center">
          بالمتابعة، أنت توافق على 
          <span className="text-yellow-400"> شروط الاستخدام </span>
          و
          <span className="text-yellow-400"> سياسة الخصوصية</span>
        </p>
      </div>
    </div>
  );
}
